import * as Sentry from '@sentry/react';
import { router } from '@inertiajs/react';
import { type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

function Fallback({ resetError }: { resetError: () => void }) {
    return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
            <AlertTriangle className="h-10 w-10 text-destructive" />
            <div className="space-y-1">
                <h1 className="text-lg font-semibold">Something went wrong</h1>
                <p className="max-w-md text-sm text-muted-foreground">
                    An unexpected error occurred while rendering this page. You can try again or
                    return to the dashboard.
                </p>
            </div>
            <div className="flex gap-2">
                <Button variant="outline" onClick={() => resetError()}>
                    Try again
                </Button>
                <Button
                    onClick={() => {
                        resetError();
                        router.visit('/dashboard');
                    }}
                >
                    Go to dashboard
                </Button>
            </div>
        </div>
    );
}

/**
 * App-wide React error boundary. Catches render/runtime errors in any page,
 * shows a recoverable fallback, and reports to Sentry when configured.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
    return (
        <Sentry.ErrorBoundary fallback={({ resetError }) => <Fallback resetError={resetError} />}>
            {children}
        </Sentry.ErrorBoundary>
    );
}
